import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { getStatusBadge, getEventBadge, formatYesNoUnknown, formatYesNoUnknownInverse, SourceIcons } from '@/lib/email-formatters';
import { formatRelativeDate } from '@/lib/format-relative-date';
import {
  ArrowLeft,
  Loader2,
  Globe,
  Mail,
  Server,
  AlertTriangle,
  Clock,
  History,
  Send,
  Code,
  CheckCircle,
  Eye,
  MousePointerClick,
  Activity,
  Database,
} from 'lucide-react';

export default function EmailPage() {
  const { email } = useParams<{ email: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const timezone = user?.timezone || 'UTC';
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!email) return;
    setLoading(true);
    setError(null);
    api.getEmailByAddress(decodeURIComponent(email))
      .then(setData)
      .catch((e) => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [email]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Database className="h-10 w-10 mb-3 opacity-50" />
          <p>{error || 'Email not found'}</p>
        </div>
      </div>
    );
  }

  const details = data.details;
  const brevoSummary = data.brevoSummary;
  const brevoEvents = data.brevoEvents || [];
  const history = data.history || [];
  const projects = data.projects || [];

  // Группировка событий по проектам
  const projectsMap: Record<string, string> = {};
  projects.forEach((p: { id: string; name: string }) => { projectsMap[p.id] = p.name; });
  const eventsByProject = brevoEvents.reduce((acc: Record<string, any[]>, ev: any) => {
    const projectId = ev.project_id || 'unknown';
    if (!acc[projectId]) acc[projectId] = [];
    acc[projectId].push(ev);
    return acc;
  }, {} as Record<string, any[]>);
  const projectIds = Object.keys(eventsByProject);
  const hasMultipleProjects = projectIds.length > 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-3 flex-1">
          <h1 className="text-2xl font-bold font-mono">{data.email}</h1>
          {getStatusBadge(data.status || 'unknown')}
          <SourceIcons row={{ has_usebouncer: data.has_usebouncer, has_brevo: data.has_brevo }} />
        </div>
      </div>

      {/* Top Cards */}
      {details && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {details.score !== null ? `${details.score}%` : '-'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Reason</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-mono">{details.reason || '-'}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Provider</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-mono">{details.provider || '-'}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Domain</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-mono">{details.domain_name || '-'}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Domain Info */}
        {details && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Domain Info
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden">
                <div className="grid grid-cols-2 gap-px bg-muted">
                  <div className="bg-background p-3">
                    <p className="text-xs text-muted-foreground mb-1">Domain</p>
                    <p className="font-mono text-sm">{details.domain_name || '-'}</p>
                  </div>
                  <div className="bg-background p-3">
                    <p className="text-xs text-muted-foreground mb-1">Accept All</p>
                    {formatYesNoUnknown(details.domain_accept_all)}
                  </div>
                  <div className="bg-background p-3">
                    <p className="text-xs text-muted-foreground mb-1">Disposable</p>
                    {formatYesNoUnknown(details.domain_disposable)}
                  </div>
                  <div className="bg-background p-3">
                    <p className="text-xs text-muted-foreground mb-1">Free Provider</p>
                    {formatYesNoUnknownInverse(details.domain_free)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Account Info */}
        {details && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Account Info
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden">
                <div className="grid grid-cols-3 gap-px bg-muted">
                  <div className="bg-background p-3">
                    <p className="text-xs text-muted-foreground mb-1">Role Account</p>
                    {formatYesNoUnknown(details.account_role)}
                  </div>
                  <div className="bg-background p-3">
                    <p className="text-xs text-muted-foreground mb-1">Disabled</p>
                    {formatYesNoUnknown(details.account_disabled)}
                  </div>
                  <div className="bg-background p-3">
                    <p className="text-xs text-muted-foreground mb-1">Full Mailbox</p>
                    {formatYesNoUnknown(details.account_full_mailbox)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delivery Events Summary */}
      {data.has_brevo && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Send className="h-4 w-4" />
              Delivery Events
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Sent', value: brevoSummary.sent, icon: Send, color: 'text-gray-600' },
                { label: 'Delivered', value: brevoSummary.delivered, icon: CheckCircle, color: 'text-green-600' },
                { label: 'Opened', value: brevoSummary.opened, icon: Eye, color: 'text-blue-600' },
                { label: 'Clicked', value: brevoSummary.clicked, icon: MousePointerClick, color: 'text-purple-600' },
                { label: 'Hard Bounce', value: brevoSummary.hard_bounced, icon: AlertTriangle, color: 'text-red-600' },
                { label: 'Soft Bounce', value: brevoSummary.soft_bounced, icon: AlertTriangle, color: 'text-orange-500' },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
                  <div className="flex items-center gap-2">
                    <s.icon className={`h-4 w-4 ${s.color}`} />
                    <span className="text-lg font-bold">{s.value}</span>
                  </div>
                </div>
              ))}
            </div>

            {brevoEvents.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Event Timeline
                  </h4>
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {hasMultipleProjects ? (
                      projectIds.map((projectId) => (
                        <div key={projectId} className="space-y-2">
                          <div className="sticky top-0 bg-background py-1 z-10">
                            <Badge variant="outline" className="text-xs font-medium">
                              {projectsMap[projectId] || projectId}
                            </Badge>
                          </div>
                          {eventsByProject[projectId].map((ev: any, idx: number) => (
                            <div key={`${ev.message_id}-${ev.event}-${idx}`} className="rounded-lg border p-3 space-y-1 ml-2">
                              <div className="flex items-center justify-between">
                                {getEventBadge(ev.event)}
                                <span className="text-xs text-muted-foreground">
                                  {formatRelativeDate(ev.date, timezone)}
                                </span>
                              </div>
                              {ev.subject && (
                                <p className="text-xs text-muted-foreground truncate">{ev.subject}</p>
                              )}
                              {ev.reason && (
                                <p className="text-xs text-orange-500 truncate">{ev.reason}</p>
                              )}
                              {ev.sender && (
                                <p className="text-xs text-muted-foreground">From: {ev.sender}</p>
                              )}
                              {ev.tag && (
                                <Badge variant="secondary" className="text-xs">{ev.tag}</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      ))
                    ) : (
                      brevoEvents.map((ev: any, idx: number) => (
                        <div key={`${ev.message_id}-${ev.event}-${idx}`} className="rounded-lg border p-3 space-y-1">
                          <div className="flex items-center justify-between">
                            {getEventBadge(ev.event)}
                            <span className="text-xs text-muted-foreground">
                              {formatRelativeDate(ev.date, timezone)}
                            </span>
                          </div>
                          {ev.subject && (
                            <p className="text-xs text-muted-foreground truncate">{ev.subject}</p>
                          )}
                          {ev.reason && (
                            <p className="text-xs text-orange-500 truncate">{ev.reason}</p>
                          )}
                          {ev.sender && (
                            <p className="text-xs text-muted-foreground">From: {ev.sender}</p>
                          )}
                          {ev.tag && (
                            <Badge variant="secondary" className="text-xs">{ev.tag}</Badge>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Verification History */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <History className="h-4 w-4" />
              Verification History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map((h: any) => (
                <div key={h.id} className="rounded-lg border p-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{h.project_name || 'Unknown'}</span>
                    <Badge variant={h.cached ? 'secondary' : 'outline'} className="text-xs">
                      {h.cached ? 'Cached' : 'Fresh'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{formatRelativeDate(h.created_at, timezone)}</span>
                    {h.response_time_ms !== null && (
                      <span>{h.response_time_ms}ms</span>
                    )}
                  </div>
                  {h.api_key_name && (
                    <p className="text-xs text-muted-foreground">API Key: {h.api_key_name}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* DNS Info */}
      {details && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Server className="h-4 w-4" />
                DNS Info
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border p-3 space-y-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Type</p>
                  <p className="font-mono text-sm">{details.dns_type || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Record</p>
                  <p className="font-mono text-xs break-all">{details.dns_record || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {(details.toxic || details.toxicity !== null) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Toxicity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border overflow-hidden">
                  <div className="grid grid-cols-2 gap-px bg-muted">
                    <div className="bg-background p-3">
                      <p className="text-xs text-muted-foreground mb-1">Type</p>
                      <p className="font-mono text-sm">{details.toxic || '-'}</p>
                    </div>
                    <div className="bg-background p-3">
                      <p className="text-xs text-muted-foreground mb-1">Score</p>
                      <p className="font-mono text-sm">
                        {details.toxicity !== null ? details.toxicity : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Metadata */}
      {details && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Metadata
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Verified At</span>
                <span className="font-mono">{formatRelativeDate(details.verified_at, timezone)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Expires At</span>
                <span className="font-mono">
                  {details.expires_at ? formatRelativeDate(details.expires_at, timezone) : 'Never'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Source</span>
                <span className="font-mono">{details.verification_source}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Verifications</span>
                <span className="font-mono">{details.verification_count}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Raw JSON */}
      {details?.raw_response && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Code className="h-4 w-4" />
              Raw API Response
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="rounded-lg border bg-muted p-4 text-xs overflow-x-auto max-h-96">
              {JSON.stringify(details.raw_response, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
